import Wrapper from './wrapper';
import { IWrapper, IBulkStatusUpdateItem, IBulkStatusUpdateResponse } from './types';
import {
  DocumentDataExclusionReasons,
  DocumentDataEntry,
  DocumentDataStatusFilters,
} from '../types/packager-types';

interface IDocumentDataUpdateParams {
  exclusionReason?: DocumentDataExclusionReasons;
  escalationDetail?: string;
  status?: DocumentDataStatusFilters;
}

interface IDocumentDataCreateParams {
  document: string;
  status?: DocumentDataStatusFilters;
  exclusionReason?: DocumentDataExclusionReasons;
  escalationDetail?: string;
}

interface IDocumentDataListParams {
  document?: string;
  limit?: number;
  offset?: number;
}

interface IDocumentDataListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: DocumentDataEntry[];
}

class DocumentData {
  wrapper: IWrapper;
  /**
   * @param {Wrapper} wrapper
   */
  constructor(wrapper: Wrapper) {
    this.wrapper = wrapper;
  }

  create(params: IDocumentDataCreateParams): Promise<DocumentDataEntry> {
    const body: Record<string, unknown> = { document: params.document };
    if (params.status !== undefined) body.status = params.status;
    if (params.exclusionReason !== undefined) body.exclusion_reason = params.exclusionReason;
    if (params.escalationDetail !== undefined) body.escalation_detail = params.escalationDetail;
    return this.wrapper.client.post<DocumentDataEntry, DocumentDataEntry>(
      `data-pools/${this.wrapper.dataPoolId}/documentdata/?advanced=true`,
      body
    );
  }

  list(params?: IDocumentDataListParams): Promise<IDocumentDataListResponse> {
    const queryParams = new URLSearchParams();

    if (params?.document) {
      queryParams.append('document', params.document);
    }
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params?.offset) {
      queryParams.append('offset', params.offset.toString());
    }

    const queryString = queryParams.toString();
    const url = `data-pools/${this.wrapper.dataPoolId}/documentdata${queryString ? `?${queryString}` : ''}`;

    return this.wrapper.client.get<IDocumentDataListResponse, IDocumentDataListResponse>(url);
  }

  update(docDataId: string, params: IDocumentDataUpdateParams): Promise<DocumentDataEntry> {
    const updateData: Record<string, unknown> = {};
    if (params.status !== undefined) updateData.status = params.status;
    if (params.exclusionReason !== undefined) updateData.exclusion_reason = params.exclusionReason;
    if (params.escalationDetail !== undefined) updateData.escalation_detail = params.escalationDetail;

    // ?advanced=true is required for the backend to accept the new
    // exclusion_reason / escalation_detail params and to return
    // available_transitions on the response.
    return this.wrapper.client.patch<DocumentDataEntry, DocumentDataEntry>(
      `data-pools/${this.wrapper.dataPoolId}/documentdata/${docDataId}?advanced=true`,
      updateData
    );
  }

  bulkUpdateStatus(items: IBulkStatusUpdateItem[]): Promise<IBulkStatusUpdateResponse> {
    return this.wrapper.client.patch<IBulkStatusUpdateResponse, IBulkStatusUpdateResponse>(
      `data-pools/${this.wrapper.dataPoolId}/documentdata/bulk-update/?advanced=true`,
      items
    );
  }

  promoteToParent(docDataId: string): Promise<DocumentDataEntry> {
    return this.wrapper.client.post<DocumentDataEntry, DocumentDataEntry>(
      `data-pools/${this.wrapper.dataPoolId}/documentdata/${docDataId}/promote-to-parent/`,
      {}
    );
  }
}

export default DocumentData;
